import { LootPickupEffect } from "../engine/effects/lootPickupEffect";
import type { Camera } from "./camera";
import { dpr } from "./dpr";
import { FloatingPopupRenderer, type FloatingPopupBase } from "./floatingPopupRenderer";
import { getPickupIconFilenameByKey } from "./pickupIconMap";
import * as TileImageLoader from "./tileImageLoader";

// How long the popup lives before being removed (ms)
const POPUP_DURATION_MS = 1500;
// Fraction of POPUP_DURATION_MS at which the popup starts fading out (0.0–1.0)
const POPUP_FADE_START = 0.85;
// Total pixels the popup floats upward over its lifetime
const POPUP_FLOAT_DISTANCE = 30;
// Initial Y offset in pixels — negative moves the popup above the cell center (e.g. above the player's head)
const POPUP_INITIAL_Y_OFFSET = -20;
// Weapon icon size in pixels (width and height)
const POPUP_ICON_SIZE = 20;
// Font size in pixels for the amount/label text
const POPUP_FONT_SIZE = 14;
// Background fill color (supports rgba for transparency)
const POPUP_BG_COLOR = "rgba(60, 60, 60, 0.7)";
// Text color for amounts and labels
const POPUP_TEXT_COLOR = "#ffffff";
// Horizontal padding inside the background box
const POPUP_PADDING_X = 8;
// Vertical padding inside the background box
const POPUP_PADDING_Y = 4;
// Horizontal gap between items when a box contains multiple weapons
const POPUP_ITEM_GAP = 6;

interface ActivePopup extends FloatingPopupBase {
	effect: LootPickupEffect;
	iconImages: Map<string, HTMLCanvasElement>;
}

export class LootPopupRenderer extends FloatingPopupRenderer<ActivePopup> {
	protected durationMs = POPUP_DURATION_MS;
	protected fadeStart = POPUP_FADE_START;
	protected floatDistance = POPUP_FLOAT_DISTANCE;

	addPopup(effect: LootPickupEffect, camera: Camera): void {
		const ratio = dpr();
		const center = camera.getCellCenterPixel(effect.xy);
		const screenX = center.x;
		const screenY = center.y - camera.cellHeight / 2 + POPUP_INITIAL_Y_OFFSET * ratio;

		const iconImages = new Map<string, HTMLCanvasElement>();

		const popup: ActivePopup = {
			effect,
			elapsed: 0,
			screenX,
			screenY,
			iconImages,
		};

		// Start loading icons for all items asynchronously
		const iconSize = Math.round(POPUP_ICON_SIZE * ratio);
		for (const item of effect.items) {
			const filename = getPickupIconFilenameByKey(item.pickupKey);
			if (filename) {
				TileImageLoader.getImage(filename, iconSize, iconSize).then((img) => {
					iconImages.set(item.pickupKey, img);
				});
			}
		}

		this.popups.push(popup);
	}

	protected drawContent(
		ctx: CanvasRenderingContext2D,
		popup: ActivePopup,
		yOffset: number,
		ratio: number,
	): void {
		const iconSize = Math.round(POPUP_ICON_SIZE * ratio);
		const fontSize = Math.round(POPUP_FONT_SIZE * ratio);
		const paddingX = POPUP_PADDING_X * ratio;
		const paddingY = POPUP_PADDING_Y * ratio;
		const itemGap = POPUP_ITEM_GAP * ratio;

		ctx.font = `${fontSize}px monospace`;

		// Measure total width of all items
		let totalWidth = 0;
		const itemWidths: number[] = [];
		for (let i = 0; i < popup.effect.items.length; i++) {
			const item = popup.effect.items[i];
			let itemWidth = 0;
			if (item.isInfinite) {
				itemWidth += ctx.measureText("Got ").width;
				itemWidth += iconSize;
				itemWidth += ctx.measureText("!").width;
			} else {
				itemWidth += ctx.measureText(`${item.amount}x `).width;
				itemWidth += iconSize;
			}
			itemWidths.push(itemWidth);
			totalWidth += itemWidth;
			if (i < popup.effect.items.length - 1) {
				totalWidth += itemGap;
			}
		}

		const bgWidth = totalWidth + paddingX * 2;
		const bgHeight = Math.max(iconSize, fontSize) + paddingY * 2;
		const bgX = popup.screenX - bgWidth / 2;
		const bgY = popup.screenY + yOffset - bgHeight / 2;

		// Draw background
		ctx.fillStyle = POPUP_BG_COLOR;
		ctx.fillRect(bgX, bgY, bgWidth, bgHeight);

		// Draw each item left-to-right
		let drawX = bgX + paddingX;
		const textY = bgY + paddingY + fontSize;
		const iconY = bgY + (bgHeight - iconSize) / 2;

		ctx.fillStyle = POPUP_TEXT_COLOR;
		for (let i = 0; i < popup.effect.items.length; i++) {
			const item = popup.effect.items[i];
			const icon = popup.iconImages.get(item.pickupKey);

			if (item.isInfinite) {
				const gotText = "Got ";
				ctx.fillStyle = POPUP_TEXT_COLOR;
				ctx.fillText(gotText, drawX, textY);
				drawX += ctx.measureText(gotText).width;
				if (icon) {
					ctx.drawImage(icon, drawX, iconY, iconSize, iconSize);
				}
				drawX += iconSize;
				ctx.fillStyle = POPUP_TEXT_COLOR;
				ctx.fillText("!", drawX, textY);
				drawX += ctx.measureText("!").width;
			} else {
				const amountText = `${item.amount}x `;
				ctx.fillStyle = POPUP_TEXT_COLOR;
				ctx.fillText(amountText, drawX, textY);
				drawX += ctx.measureText(amountText).width;
				if (icon) {
					ctx.drawImage(icon, drawX, iconY, iconSize, iconSize);
				}
				drawX += iconSize;
			}

			if (i < popup.effect.items.length - 1) {
				drawX += itemGap;
			}
		}
	}
}
